/**
 * Acquisition tracking — captures the source of a web visit so we can
 * attribute new signups to the right campaign.
 *
 * First-touch only. The first time a visitor lands on the site with any
 * recognizable source signal (utm_*, referrer from a known network, etc.)
 * we stash the snapshot in localStorage with a 30-day TTL. Subsequent
 * visits with no signal don't clobber the original — that matches how
 * acquisition_* on the backend is set: written once on signup, never
 * overwritten.
 *
 * Read alongside docs/GROWTH_STRATEGY.md §6.
 */

const STORAGE_KEY = 'lomi_acquisition';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const PARTNER_NETWORKS: Record<string, string> = {
  // Hand-curated map: known referrer hosts → coarse source bucket. Anything
  // not in this map and not utm-tagged is treated as 'organic' and skipped.
  'tiktok.com': 'tiktok',
  'www.tiktok.com': 'tiktok',
  't.me': 'telegram',
  'web.telegram.org': 'telegram',
  'instagram.com': 'instagram',
  'www.instagram.com': 'instagram',
  'facebook.com': 'facebook',
  'm.facebook.com': 'facebook',
  'youtube.com': 'youtube',
  'www.youtube.com': 'youtube',
  'twitter.com': 'twitter',
  'x.com': 'twitter',
  'google.com': 'google',
  'www.google.com': 'google',
};

export interface AcquisitionPayload {
  source: string | null;        // 'school','influencer','tiktok','google','organic',…
  campaign: string | null;      // utm_campaign or hand-set
  medium: string | null;        // utm_medium ('cpc','social','print',…)
  term: string | null;          // utm_term
  content: string | null;       // utm_content
  referrer: string | null;      // document.referrer host
  referral_code: string | null; // ?ref=<code> from a peer invite
  raw_query: string | null;     // verbatim ?... for forensics
  captured_at: number;          // epoch ms
}

interface Stored extends AcquisitionPayload {
  // Only the captured_at timestamp is used to TTL the record; it lives
  // alongside the payload so a single read covers both.
  expires_at: number;
}

/**
 * Read URL + referrer once and decide whether to persist a record.
 *
 * Returns the new payload if we wrote one this call, or the existing one
 * if it was already set, or null if there's no signal at all.
 */
export function captureAcquisition(): AcquisitionPayload | null {
  if (typeof window === 'undefined') return null;

  // Already captured and not expired? Don't clobber — first-touch wins.
  const existing = loadStoredAcquisition();
  if (existing) return existing;

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmCampaign = params.get('utm_campaign');
  const utmMedium = params.get('utm_medium');
  const utmTerm = params.get('utm_term');
  const utmContent = params.get('utm_content');
  const gclid = params.get('gclid');
  const fbclid = params.get('fbclid');
  // Refer-a-friend code from a peer invite. Validated server-side at
  // register; we just carry it through.
  const refRaw = params.get('ref');
  const referralCode = refRaw && /^[A-Za-z0-9_-]{4,16}$/.test(refRaw) ? refRaw : null;

  const referrerHost = parseReferrerHost(document.referrer);
  const referrerNetwork = referrerHost ? PARTNER_NETWORKS[referrerHost] : null;

  // Decide source. Priority: an explicit referral code (peer invite),
  // then utm_source, then ad-network click ids, then a recognized
  // referrer network. Otherwise no signal — bail out so we don't pollute
  // with empty rows.
  let source: string | null = referralCode ? 'referral' : utmSource;
  if (!source && gclid) source = 'google_ads';
  if (!source && fbclid) source = 'facebook_ads';
  if (!source && referrerNetwork) source = referrerNetwork;

  if (!source) return null;

  const payload: AcquisitionPayload = {
    source,
    campaign: utmCampaign || (referralCode ? referralCode : null),
    medium: utmMedium,
    term: utmTerm,
    content: utmContent,
    referrer: referrerHost,
    referral_code: referralCode,
    raw_query: window.location.search || null,
    captured_at: Date.now(),
  };

  try {
    const stored: Stored = { ...payload, expires_at: Date.now() + TTL_MS };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // localStorage might be disabled (Safari private). Treat as in-memory
    // only — the register call later in this session can still read it
    // from the return value if the caller holds onto it.
  }

  return payload;
}

/**
 * Load the persisted acquisition payload if still within TTL.
 * Used both internally (to avoid clobber) and externally (by the register
 * call, to attach acquisition to the signup body).
 */
export function loadStoredAcquisition(): AcquisitionPayload | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed.expires_at || parsed.expires_at < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    // Strip the expires_at field on the way out — callers only care about
    // the payload shape that gets sent to the API.
    const { expires_at: _ignored, ...payload } = parsed;
    return payload;
  } catch {
    return null;
  }
}

/**
 * Stamp acquisition from a path-based landing (e.g. /r/lomi42) — no URL
 * params needed. Mirrors captureAcquisition's first-touch-wins behavior:
 * if a record already exists, leave it; otherwise write a synthetic
 * payload pointing at the slug.
 *
 * The slug lands in `campaign` so /ops/growth's per-campaign rollups
 * group bot (`acquisition_campaign='lomi42'`) and web together.
 */
export function captureSlugAcquisition(slug: string, source = 'influencer'): AcquisitionPayload | null {
  if (typeof window === 'undefined') return null;
  if (!/^[a-z0-9]{2,32}$/i.test(slug)) return null;

  const existing = loadStoredAcquisition();
  if (existing) return existing;

  const payload: AcquisitionPayload = {
    source,
    campaign: slug,
    medium: null,
    term: null,
    content: null,
    referrer: parseReferrerHost(document.referrer),
    referral_code: null,
    raw_query: `/r/${slug}`,
    captured_at: Date.now(),
  };

  try {
    const stored: Stored = { ...payload, expires_at: Date.now() + TTL_MS };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Safari Private — same fallback as captureAcquisition.
  }
  return payload;
}

/**
 * Clear the stored payload. Call after a successful register so a single
 * device used by two siblings doesn't carry the first signup's source
 * onto the second.
 */
export function clearAcquisition(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function parseReferrerHost(ref: string | null | undefined): string | null {
  if (!ref) return null;
  try {
    return new URL(ref).host || null;
  } catch {
    return null;
  }
}
